/**
 * Centralized logging utility for the frontend
 * Respects NODE_ENV and provides structured logging
 */

/* eslint-disable no-console */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

class Logger {
  constructor() {
    // isProduction must be set before getLogLevel() — otherwise logLevel stays DEBUG in production
    this.isProduction = process.env.NODE_ENV === 'production';
    this.logLevel = this.getLogLevel();
  }

  getLogLevel() {
    const envLogLevel = process.env.REACT_APP_LOG_LEVEL?.toUpperCase();
    if (envLogLevel && LOG_LEVELS.hasOwnProperty(envLogLevel)) {
      return LOG_LEVELS[envLogLevel];
    }
    // In production, only show ERROR and WARN, suppress INFO and DEBUG
    return this.isProduction ? LOG_LEVELS.WARN : LOG_LEVELS.DEBUG;
  }

  shouldLog(level) {
    return level <= this.logLevel;
  }

  formatMessage(level, message, data = null) {
    const timestamp = new Date().toISOString();
    const levelName = Object.keys(LOG_LEVELS)[level];

    if (data) {
      return [`[${timestamp}] ${levelName}: ${message}`, data];
    }
    return [`[${timestamp}] ${levelName}: ${message}`];
  }

  error(message, data = null) {
    if (this.shouldLog(LOG_LEVELS.ERROR)) {
      const [formattedMessage, ...args] = this.formatMessage(LOG_LEVELS.ERROR, message, data);
      console.error(formattedMessage, ...args);
    }
  }

  warn(message, data = null) {
    if (this.shouldLog(LOG_LEVELS.WARN)) {
      const [formattedMessage, ...args] = this.formatMessage(LOG_LEVELS.WARN, message, data);
      console.warn(formattedMessage, ...args);
    }
  }

  info(message, data = null) {
    if (this.shouldLog(LOG_LEVELS.INFO)) {
      const [formattedMessage, ...args] = this.formatMessage(LOG_LEVELS.INFO, message, data);
      console.info(formattedMessage, ...args);
    }
  }

  debug(message, data = null) {
    try {
      if (this && this.shouldLog && this.shouldLog(LOG_LEVELS.DEBUG)) {
        const [formattedMessage, ...args] = this.formatMessage(LOG_LEVELS.DEBUG, message, data);
        console.log(formattedMessage, ...args);
      }
    } catch (err) {
      // Fallback if 'this' context is lost
      console.log(message, data);
    }
  }

  // Firebase-specific logging methods
  firebaseError(operation, error) {
    this.error(`Firebase error in ${operation}`, {
      code: error.code,
      message: error.message,
      operation
    });
  }

  firebaseSuccess(operation, data = null) {
    this.info(`Firebase operation successful: ${operation}`, data);
  }

  // API-specific logging methods
  apiRequest(method, url, data = null) {
    this.debug(`API Request: ${method} ${url}`, data);
  }

  apiResponse(method, url, status, data = null) {
    const level = status >= 400 ? LOG_LEVELS.ERROR : LOG_LEVELS.DEBUG;
    if (this.shouldLog(level)) {
      const [formattedMessage, ...args] = this.formatMessage(
        level,
        `API Response: ${method} ${url} - ${status}`,
        data
      );
      console[level === LOG_LEVELS.ERROR ? 'error' : 'log'](formattedMessage, ...args);
    }
  }

  // User action logging
  userAction(action, details = null) {
    this.info(`User action: ${action}`, details);
  }

  // Performance logging
  performance(operation, duration, details = null) {
    this.debug(`Performance: ${operation} took ${duration}ms`, details);
  }
}

// Create singleton instance
const logger = new Logger();

// Bind methods to ensure 'this' context is always correct
logger.debug = logger.debug.bind(logger);
logger.info = logger.info.bind(logger);
logger.warn = logger.warn.bind(logger);
logger.error = logger.error.bind(logger);

// Export both the class and instance
export { Logger, LOG_LEVELS };
export default logger;
